import React, {useState} from 'react';

import TabbedModal, {
  StyledModalFooter,
} from 'app/components/general/TabbedModal';
import {useMoveFolder} from 'app/folders/api';
import StateButton from 'app/general/widgets/StateButton';
import {useModal} from 'app/utils/hooks';
import {gt} from 'app/utils/i18n';

import {FolderSelection} from './FolderSelection';

import type {SelectedItemType} from 'app/kbs/components/KBEditPage/KBTree/KBTreeDeleteItemModal';
import type {FolderSerializedType} from 'sweet-types';

export const useMoveModal = () => {
  return useModal({Modal: MoveItemModal});
};

type Props<T> = {
  item: SelectedItemType<T>;
  onClose: () => unknown;
  show: boolean;
};

export const MoveItemModal = ({
  item,
  onClose,
  show,
}: Props<FolderSerializedType>) => {
  const [selectedFolder, setSelectedFolder] =
    useState<FolderSerializedType | null>(null);
  const [moveFolder] = useMoveFolder();

  const move = () => {
    if (selectedFolder) {
      moveFolder({
        hashid: item.object.hashid,
        folder: item.object.url,
        parent: selectedFolder.url != '' ? selectedFolder.url : null, // optionally cancel out the parent.
      }).then(() => {
        if (item.callback != null) {
          item.callback();
        }
      });
      onClose();
      setSelectedFolder(null); // Reset the selected folder state
    }
  };

  const name = <b>{item.object.name}</b>;
  const title = item.title ?? gt('Move Folder');
  const icon = item.icon ?? 'far fa-folder';
  const description =
    item?.description ?? gt('Are you sure you want to move %s?', name);
  const help = item?.help ?? null;

  function actionsGetter(folderParent: FolderSerializedType) {
    return [
      {
        text: gt('Select this folder'),
        key: 'select-folder',
        icon: 'fas fa-plus',
        condition: () =>
          item.object.id != folderParent.id &&
          item.object.parent != folderParent.hashid &&
          folderParent.parent != item.object.hashid,
        callback: () => {
          setSelectedFolder(folderParent);
        },
      },
    ];
  }

  return (
    <TabbedModal
      show={show}
      onCancel={onClose}
      tabs={[
        {
          eventKey: 'move-object',
          name: title,
          icon,
          content: (
            <div>
              <p>{description}</p>
              <p>{help}</p>
              {!selectedFolder ? (
                <>
                  {item.object.parent != null && (
                    <button
                      className="btn btn-link text-primary pl-0"
                      onClick={() => {
                        const main = {
                          ...item.object,
                          hashid: '',
                          url: '',
                          id: 0,
                          name: gt('Top Level'),
                        };
                        setSelectedFolder(main);
                      }}
                    >
                      <i className="far fa-folder mr-1" />
                      {gt('Move up to the top level')}
                    </button>
                  )}
                  <FolderSelection
                    actionsGetter={actionsGetter}
                    refreshPosition={() => undefined}
                  />
                </>
              ) : (
                <SelectedFolderInfo
                  child={item.object}
                  parent={selectedFolder}
                />
              )}

              <StyledModalFooter>
                <button
                  onClick={onClose}
                  className="btn btn-link"
                  data-dismiss="modal"
                >
                  <i className="fas fa-times mr-2" />
                  {gt('Cancel')}
                </button>
                <StateButton
                  disabled={!selectedFolder}
                  onClick={move}
                  busyText={title}
                  className="btn btn-primary"
                  data-dismiss="modal"
                >
                  {title}
                </StateButton>
              </StyledModalFooter>
            </div>
          ),
        },
      ]}
    />
  );
};

type InfoProps = {
  child: FolderSerializedType;
  parent: FolderSerializedType;
};
const SelectedFolderInfo = ({child, parent}: InfoProps) => {
  return (
    <div className="mb-3">
      <FolderDisplay
        className=""
        folderName={parent.name}
        teams={parent.teams}
      />

      <div className="d-flex my-1">
        <div className="mx-3">
          <span className="fa-rotate-270 d-inline-block">
            <i className="fas fa-level-down-alt fa-flip-horizontal" />
          </span>
        </div>
        <FolderDisplay
          className="font-weight-bold"
          folderName={child.name}
          teams={parent.teams}
        />
      </div>
    </div>
  );
};

const FolderDisplay = ({folderName, teams, className}) => {
  return (
    <div>
      <div className="row">
        <div className="col-auto pr-0">
          <i className="far fa-folder" />
        </div>
        <div className="col">
          <div className={className}>{folderName}</div>
          <div className="team-badges">
            {teams.map((team) => (
              <span key={team.url} className="badge badge-light mr-1">
                {team.name}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
